import { useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { supportLabels } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HowAuctionsWork } from "./document-guides/HowAuctionsWork";
import { PaymentDelivery } from "./document-guides/PaymentDelivery";
import { BeforeBidAndPurchase } from "./document-guides/BeforeBidAndPurchase";
import { DeliveryChecklist } from "./document-guides/DeliveryChecklist";
import { IfSomethingWrong } from "./document-guides/IfSomethingWrong";

interface DocumentModalProps {
  guideId: string;
  guide: {
    title: string;
    description: string;
    duration: string;
    type: "video" | "document";
  } | null;
  onClose: () => void;
}

const DocumentModal = ({ guideId, guide, onClose }: DocumentModalProps) => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const supportLabels_i18n = supportLabels(language);
  const isDark = theme === "dark";

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (guide) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [guide]);

  if (!guide) return null;

  const renderGuideContent = () => {
    switch (guideId) {
      case "howAuctionsWork":
      case "how-auctions-work":
        return <HowAuctionsWork />;
      case "paymentDelivery":
      case "payment-delivery":
        return <PaymentDelivery />;
      case "beforeBidAndPurchase":
      case "before-bid-and-purchase":
        return <BeforeBidAndPurchase />;
      case "deliveryChecklist":
      case "delivery-checklist":
        return <DeliveryChecklist />;
      case "ifSomethingWrong":
      case "if-something-wrong":
        return <IfSomethingWrong />;
      default:
        return (
          <div
            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
          >
            {supportLabels_i18n.contentComingSoon}
          </div>
        );
    }
  };

  return (
    <Dialog open={!!guide} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl md:text-2xl pr-8 break-words">
            {guide.title}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge
              className={`text-xs ${
                isDark
                  ? "bg-blue-500/10 text-blue-400"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {supportLabels_i18n.guides?.documentType ?? "Document"}
            </Badge>
            <span
              className={`text-xs ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {guide.duration}
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 mt-4">
          <div
            className={`space-y-4 ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {renderGuideContent()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentModal;
